import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  getAdminTrainingBlockAssetUrl,
  getTrainingBlockAssetUrl,
  getTrainingMeStatus,
  getTrainingModule,
  getTrainingModulePreview,
  getTrainingModuleProgress,
  getTrainingQuizDraftAnswers,
  listTrainingModules,
  postTrainingBlockProgress,
  saveTrainingQuizDraftAnswer,
  submitTrainingQuizAttempt,
  type TrainingBlock,
  type TrainingBlockAssetUrl,
  type TrainingMeStatusItem,
  type TrainingModule,
  type TrainingModuleListItem,
  type TrainingModulePreview,
  type TrainingModuleProgress,
  type TrainingQuizAttemptResult,
  type TrainingQuizQuestion,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import {
  TrainingCompletionConfirmation,
  type TrainingCompletionVariant,
} from "@/components/training/TrainingCompletionConfirmation";
import { VideoPlayer } from "@/components/training/VideoPlayer";
import { useTrainingLockout } from "@/lib/useTrainingLockout";
import { clearTrainingReturn, peekTrainingReturn } from "@/lib/trainingReturn";
import { toYouTubeEmbedUrl } from "@/lib/youtube";

// Block types whose binary is fetched via the presigned asset-url endpoint.
const MEDIA_TYPES = new Set(["video_minio", "pdf", "powerpoint", "image"]);

// CHP-898/953: marking a block reviewed is no longer per-block. The single
// "Confirm review and unlock quiz" gate (ConfirmReviewGate) records progress for
// every still-incomplete required block at once. A required video_minio posts
// its full duration so the 95% watch gate is satisfied; everything else posts 1.
function reviewWatchedSeconds(block: TrainingBlock): number {
  return block.block_type === "video_minio" ? (block.duration_seconds ?? 1) : 1;
}

// CHP-421: admin "preview as a trainee" config. Its presence switches the player
// into a read-only preview — media resolves via the admin asset-url (so a
// draft/revision's binaries render), progress isn't fetched or written, the quiz
// is unlocked and scored client-side from `correctByQuestion` (question_id →
// correct option_id) rather than submitted.
export interface ModulePreviewConfig {
  correctByQuestion: Record<string, string>;
}

// A human label for a block in the checklist / unmet-prerequisites list, falling
// back to a type name when the author gave the block no title.
const BLOCK_TYPE_LABELS: Record<TrainingBlock["block_type"], string> = {
  video_minio: "Video",
  video_youtube: "Video",
  pdf: "Document",
  powerpoint: "Slides",
  image: "Image",
  text: "Reading",
};

function blockLabel(block: TrainingBlock): string {
  return block.title ?? BLOCK_TYPE_LABELS[block.block_type];
}

/**
 * Renders one content block. Media blocks (video/pdf/powerpoint/image) resolve
 * a short-lived presigned URL on demand; text renders its body; a YouTube block
 * embeds its external_url. Recording that required blocks were reviewed is
 * handled by the single "Confirm review and unlock quiz" gate below
 * (CHP-898/953), not per block.
 */
function BlockView({
  block,
  moduleId,
  token,
  preview = false,
  viewOnly = false,
}: {
  block: TrainingBlock;
  moduleId: string;
  token: string;
  preview?: boolean;
  /** CHP-1013: view-only (CM) — trainee asset-url (published version), but no
   * watch-progress writes. */
  viewOnly?: boolean;
}) {
  const isMedia = MEDIA_TYPES.has(block.block_type);
  const asset = useQuery<TrainingBlockAssetUrl>({
    // Preview resolves media via the admin endpoint (any version), so a
    // draft/revision's binaries render; the key is namespaced to avoid sharing
    // a cache entry with the trainee (published-only) URL.
    queryKey: ["training", preview ? "admin-asset-url" : "asset-url", moduleId, block.id],
    queryFn: () =>
      preview
        ? getAdminTrainingBlockAssetUrl(token, moduleId, block.id)
        : getTrainingBlockAssetUrl(token, moduleId, block.id),
    enabled: isMedia,
    // Refetch comfortably before the 10-minute presign expiry.
    staleTime: 9 * 60 * 1000,
  });

  return (
    <div className="rd-card" style={{ padding: 18, marginBottom: 14 }}>
      {block.title && (
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>{block.title}</div>
      )}

      {block.block_type === "text" && (
        <div
          style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}
        >
          {block.body}
        </div>
      )}

      {block.block_type === "video_youtube" && block.external_url && (
        <div style={{ aspectRatio: "16/9" }}>
          <iframe
            src={toYouTubeEmbedUrl(block.external_url)}
            title={block.title ?? "Training video"}
            style={{ width: "100%", height: "100%", border: 0, borderRadius: 10 }}
            allowFullScreen
          />
        </div>
      )}

      {isMedia && asset.isLoading && <div className="sub">Loading…</div>}
      {isMedia && asset.isError && (
        <div className="sub">This media is unavailable right now. Please try again.</div>
      )}
      {isMedia && asset.data && (
        <MediaAsset
          blockType={block.block_type}
          url={asset.data.url}
          title={block.title}
          blockId={block.id}
          token={token}
          trackProgress={!viewOnly}
        />
      )}
    </div>
  );
}

/**
 * CHP-898/953: the single "Confirm review and unlock quiz" gate. Replaces the
 * old per-block "Mark as reviewed" buttons with one deliberate attestation that
 * records progress for every still-incomplete required block (the backend marks
 * a block complete on a progress POST), then unlocks the quiz. Invalidates the
 * hub status + module-progress queries so the checklist, counters, and quiz gate
 * refresh. `hasQuiz` only adjusts the label — a quizless module just confirms
 * the review to complete.
 */
function ConfirmReviewGate({
  token,
  unmetBlocks,
  hasQuiz,
}: {
  token: string;
  unmetBlocks: TrainingBlock[];
  hasQuiz: boolean;
}) {
  const queryClient = useQueryClient();

  const confirm = useMutation({
    mutationFn: async () => {
      for (const block of unmetBlocks) {
        await postTrainingBlockProgress(token, block.id, reviewWatchedSeconds(block));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["training", "me-status"] });
      void queryClient.invalidateQueries({ queryKey: ["training", "module-progress"] });
    },
  });

  return (
    <div className="rd-card" style={{ padding: 18, marginBottom: 20 }} data-testid="confirm-review">
      <button
        type="button"
        className="rd-btn primary"
        disabled={confirm.isPending}
        onClick={() => confirm.mutate()}
        data-testid="confirm-review-gate"
      >
        {confirm.isPending
          ? "Confirming…"
          : hasQuiz
            ? "Confirm review and unlock quiz"
            : "Confirm review"}
      </button>
      {confirm.isError && (
        <div className="sub" style={{ marginTop: 8 }}>
          Couldn’t confirm just now. Please try again.
        </div>
      )}
    </div>
  );
}

function MediaAsset({
  blockType,
  url,
  title,
  blockId,
  token,
  trackProgress = true,
}: {
  blockType: TrainingBlock["block_type"];
  url: string;
  title: string | null;
  blockId: string;
  token: string;
  trackProgress?: boolean;
}) {
  if (blockType === "video_minio") {
    return (
      <VideoPlayer
        src={url}
        title={title}
        blockId={blockId}
        token={token}
        trackProgress={trackProgress}
      />
    );
  }
  if (blockType === "image") {
    return (
      <img
        src={url}
        alt={title ?? "Training image"}
        style={{ maxWidth: "100%", borderRadius: 10 }}
      />
    );
  }
  if (blockType === "pdf") {
    return (
      <iframe
        src={url}
        title={title ?? "Training document"}
        style={{ width: "100%", height: 600, border: 0, borderRadius: 10 }}
      />
    );
  }
  // powerpoint (and any other downloadable) — open/download in a new tab.
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="rd-btn ghost sm">
      Open {title ?? "file"}
    </a>
  );
}

// CHP-1049: question_id → correct option_id, built from the RA preview payload
// (the trainee shape omits is_correct; the preview endpoint carries it). Feeds
// the quiz's answers-marked rendering. Same shape as the admin preview helper.
function correctByQuestion(m: TrainingModulePreview): Record<string, string> {
  const map: Record<string, string> = {};
  for (const q of m.current_version?.quiz_questions ?? []) {
    const correct = q.options.find((o) => o.is_correct);
    if (correct) map[q.id] = correct.id;
  }
  return map;
}

export function ModulePlayer({
  token,
  moduleId,
  viewOnly = false,
  withAnswers = false,
}: {
  token: string;
  moduleId: string;
  viewOnly?: boolean;
  /** CHP-1049: RA read-only preview — fetch the `require_admin`-gated preview
   * endpoint (answer key included) and render the quiz with the correct answers
   * marked. Distinct from `viewOnly` (CM), which hides the quiz entirely. */
  withAnswers?: boolean;
}) {
  const module = useQuery<TrainingModule | TrainingModulePreview>({
    queryKey: ["training", withAnswers ? "module-preview" : "module", moduleId],
    queryFn: () =>
      withAnswers ? getTrainingModulePreview(token, moduleId) : getTrainingModule(token, moduleId),
  });

  const answerKey =
    withAnswers && module.data
      ? correctByQuestion(module.data as TrainingModulePreview)
      : undefined;

  return (
    <div className="demo-theme">
      <div className="page">
        <div style={{ marginBottom: 16 }}>
          <Link to="/dashboard/partner/training" className="rd-btn ghost sm">
            ← Back to Training hub
          </Link>
        </div>

        {module.isLoading ? (
          <div className="sub">Loading…</div>
        ) : module.isError || !module.data ? (
          <div className="sub">This module isn’t available.</div>
        ) : (
          <ModuleBody
            token={token}
            module={module.data}
            viewOnly={viewOnly}
            answerKey={answerKey}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Completion checklist (RACCPE-28): every block with its required/optional tag
 * and viewed/✓ state, plus a "what's left" summary of the required steps. Gives
 * the trainee an explicit picture of what completion needs before they reach the
 * quiz — the silent partial state was what made the RACCPE-22 lockout feel broken.
 */
function CompletionChecklist({
  blocks,
  completedIds,
}: {
  blocks: TrainingBlock[];
  completedIds: Set<string>;
}) {
  const required = blocks.filter((b) => b.required_for_completion);
  const requiredDone = required.filter((b) => completedIds.has(b.id)).length;
  const remaining = required.length - requiredDone;

  return (
    <div
      className="rd-card"
      style={{ padding: 18, marginBottom: 20 }}
      data-testid="completion-checklist"
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Your progress</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {blocks.map((b) => {
          const done = completedIds.has(b.id);
          return (
            <div
              key={b.id}
              style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13 }}
            >
              <span aria-label={done ? "Done" : "Not done"}>{done ? "✓" : "◻"}</span>
              <span>{blockLabel(b)}</span>
              <span className="sub" style={{ marginLeft: "auto" }}>
                {b.required_for_completion ? "required" : "optional"}
              </span>
            </div>
          );
        })}
      </div>
      {required.length > 0 && (
        <div className="sub" style={{ marginTop: 12 }} data-testid="checklist-summary">
          {requiredDone} of {required.length} required steps done
          {remaining > 0 ? ` — ${remaining} to go` : " — take the quiz to finish"}
        </div>
      )}
    </div>
  );
}

/**
 * Deals-gap W2: "Continue registering your deal" CTA. Shown after a module
 * completes IF (a) the user was sent here from the deal-creation block screen
 * (a return target is persisted in sessionStorage — lib/trainingReturn.ts)
 * AND (b) their Level 1 Certification is now actually complete (every
 * compulsory module completed) — finishing one module of several shouldn't
 * bounce them back to a screen that will still block them. The stored draftId
 * (if any) is intentionally dropped on return: the wizard restarts fresh and
 * the draft remains reachable from the drafts list.
 */
function ReturnToDealCta({ token }: { token: string }) {
  const target = peekTrainingReturn();
  const statuses = useQuery<TrainingMeStatusItem[]>({
    queryKey: ["training", "me-status"],
    queryFn: () => getTrainingMeStatus(token),
    enabled: target !== null,
  });

  if (!target || !statuses.data) return null;
  const compulsory = statuses.data.filter((s) => s.is_compulsory);
  if (!compulsory.every((s) => s.status === "completed")) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Link
        to="/dashboard/partner/deals/new"
        className="rd-btn"
        onClick={clearTrainingReturn}
        data-testid="return-to-deal-cta"
      >
        Continue registering your deal →
      </Link>
    </div>
  );
}

export function ModuleBody({
  token,
  module,
  preview,
  viewOnly = false,
  answerKey,
}: {
  token: string;
  module: TrainingModule;
  preview?: ModulePreviewConfig;
  /** CHP-1013: a view-only reader (CM) — like preview, no progress is fetched
   * or written, but media resolves via the TRAINEE asset-url (published
   * version) and the quiz is hidden entirely rather than client-scored. */
  viewOnly?: boolean;
  /** CHP-1049: RA read-only preview (question_id → correct option_id). Read-only
   * like viewOnly (trainee asset-url, no progress), but the quiz is SHOWN with
   * the correct answers marked rather than hidden. */
  answerKey?: Record<string, string>;
}) {
  const blocks = module.current_version?.blocks ?? [];
  const questions = module.current_version?.quiz_questions ?? [];
  const readOnly = !!preview || viewOnly || !!answerKey;
  const { data: user } = useCurrentUser();
  // Subscribes to ["training","me-status"], which the quiz submit invalidates on
  // a completing pass — so after `onCompleted` fires (post-refetch) the
  // outstanding count here is the settled, post-completion value (CHP-443).
  // CHP-809 (#26): in preview the result only feeds completionVariant, which a
  // (justCompleted && !preview) branch never reaches — so skip the fetch.
  const lockout = useTrainingLockout({ enabled: !readOnly });

  // A completion that just happened in this session shows the confirmation
  // screen; revisiting an already-complete module does not (the screen is
  // transient, reset on navigation away).
  const [justCompleted, setJustCompleted] = useState(false);

  const progress = useQuery<TrainingModuleProgress>({
    queryKey: ["training", "module-progress", module.id],
    queryFn: () => getTrainingModuleProgress(token, module.id),
    enabled: !readOnly,
  });

  // CHP-897: the module's position in the published learning path, surfaced as a
  // "Step N of M" eyebrow. Shares the hub's module-list cache; hidden in preview
  // and falls back to "Training" when the module isn't resolvable in the list.
  const moduleList = useQuery<TrainingModuleListItem[]>({
    queryKey: ["training", "modules"],
    queryFn: () => listTrainingModules(token),
    enabled: !readOnly,
  });
  const stepIndex = moduleList.data?.findIndex((m) => m.id === module.id) ?? -1;
  const stepTotal = moduleList.data?.length ?? 0;
  const stepLabel =
    !readOnly && stepIndex >= 0 && stepTotal > 0
      ? `Step ${stepIndex + 1} of ${stepTotal}`
      : "Training";

  const completedIds = new Set(progress.data?.completed_block_ids ?? []);
  const unmetRequired = blocks.filter((b) => b.required_for_completion && !completedIds.has(b.id));
  // Preview / RA answer-key view are read-only (no progress): the quiz shows
  // unlocked and the progress-driven chrome (checklist, completion banner,
  // return CTA) is hidden.
  const quizUnlocked = preview || answerKey ? true : unmetRequired.length === 0;
  const moduleCompleted = readOnly ? false : (progress.data?.completed ?? false);

  // CHP-443 / CHP-1047 — pick the post-completion screen. The certification /
  // deal-reg unlock claim is true for any gate-eligible channel_partner (BDM or
  // PO), not exempted, who just cleared their LAST outstanding compulsory module.
  // Managers, exempted partners, and optional-module completions get a neutral
  // "Module complete". Mirrors useTrainingLockout's gate-eligibility test.
  const isGateEligiblePartner = user?.role === "channel_partner";
  const exempted = Boolean(user?.training_deal_block_exempted);
  const completionVariant: TrainingCompletionVariant =
    isGateEligiblePartner && !exempted && module.is_compulsory
      ? lockout.outstandingCount === 0
        ? "certified"
        : "keep-going"
      : "neutral";

  if (justCompleted && !preview) {
    return <TrainingCompletionConfirmation variant={completionVariant} moduleName={module.title} />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          {/* CHP-897: "Step N of M" eyebrow + a Mandatory status pill alongside
              the module title when it's compulsory. */}
          <div className="h-eyebrow">{stepLabel}</div>
          <h1
            className="h1"
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {module.title}
            {module.is_compulsory && <span className="chip accent">Mandatory</span>}
          </h1>
          {module.description && <div className="sub">{module.description}</div>}
        </div>
      </div>

      {moduleCompleted && (
        <div className="chip accent" style={{ marginBottom: 16 }} data-testid="module-complete">
          <span className="dot ok" /> Module complete
        </div>
      )}

      {moduleCompleted && <ReturnToDealCta token={token} />}

      {/* CHP-809 (#22): an empty draft (no blocks, no quiz) otherwise renders
          a near-blank page below the header in preview — show an explicit
          "no content yet" state so the author knows nothing's there to see. */}
      {preview && blocks.length === 0 && questions.length === 0 ? (
        <div className="card" data-testid="preview-empty-content" style={{ marginBottom: 16 }}>
          <div className="sub">
            This module has no content yet. Add blocks or quiz questions in the editor to see them
            here.
          </div>
        </div>
      ) : null}

      {!readOnly && blocks.length > 0 && (
        <CompletionChecklist blocks={blocks} completedIds={completedIds} />
      )}

      <div style={{ marginBottom: 28 }}>
        {blocks.map((b) => (
          <BlockView
            key={b.id}
            block={b}
            moduleId={module.id}
            token={token}
            preview={!!preview}
            viewOnly={viewOnly || !!answerKey}
          />
        ))}
      </div>

      {/* CHP-898/953: a single "Confirm review and unlock quiz" gate. Shown
          while there are still-incomplete required blocks; pressing it records
          their progress and unlocks the quiz. */}
      {!readOnly && unmetRequired.length > 0 && (
        <ConfirmReviewGate
          token={token}
          unmetBlocks={unmetRequired}
          hasQuiz={questions.length > 0}
        />
      )}

      {/* CHP-1013: view-only (CM) hides the quiz outright — no attempts, no
          client-side scoring; they read the content, nothing more. CHP-1049: an
          RA answer-key view instead SHOWS the quiz static, with the correct
          answers marked (QuizAnswerKey) — no submission. Trainees/authoring
          preview get the interactive Quiz. */}
      {answerKey
        ? questions.length > 0 && <QuizAnswerKey questions={questions} answerKey={answerKey} />
        : !viewOnly &&
          questions.length > 0 && (
            <Quiz
              token={token}
              moduleId={module.id}
              questions={questions}
              locked={!quizUnlocked}
              preview={preview}
              onCompleted={() => setJustCompleted(true)}
            />
          )}
    </>
  );
}

/**
 * Interactive module quiz (RACCPE-14). Renders questions/options in the order
 * the API returns them (already shuffled server-side when quiz_randomise is on),
 * submits the selected answers, and shows the scored result. 100% is required
 * to pass; retries are unlimited. On a pass that completes the module, the
 * trainee status query is invalidated so the hub counters refresh.
 */
/**
 * CHP-1049: the quiz as a trainee sees it, rendered STATIC and read-only with
 * the correct option marked — no inputs, no submission, no draft writes. Used by
 * the RA answer-key preview. A separate component (not a Quiz mode) so the
 * interactive Quiz's hooks never run in this read-only path.
 */
function QuizAnswerKey({
  questions,
  answerKey,
}: {
  questions: TrainingQuizQuestion[];
  answerKey: Record<string, string>;
}) {
  return (
    <div>
      <h2 className="h2" style={{ marginBottom: 6 }}>
        Quiz
      </h2>
      <div className="sub" style={{ marginBottom: 14 }}>
        The quiz as a trainee sees it, with the correct answer marked. Read-only preview — no
        answers are submitted.
      </div>
      <div>
        {questions.map((q, qi) => (
          <div key={q.id} className="rd-card" style={{ padding: 18, marginBottom: 14 }}>
            <div id={`quiz-q-${q.id}`} style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
              {qi + 1}. {q.prompt}
            </div>
            <div
              role="list"
              aria-labelledby={`quiz-q-${q.id}`}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {q.options.map((o) => {
                const isCorrect = answerKey[q.id] === o.id;
                return (
                  <div
                    key={o.id}
                    role="listitem"
                    data-testid={isCorrect ? "quiz-correct-option" : undefined}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      fontSize: 13,
                      fontWeight: isCorrect ? 600 : 400,
                    }}
                  >
                    <span aria-hidden>{isCorrect ? "✓" : "○"}</span>
                    <span>{o.label}</span>
                    {/* Text chip (not colour alone) carries the accessible
                        meaning — WCAG 1.4.1; announced once by AT. */}
                    {isCorrect && (
                      <span className="chip accent" style={{ marginLeft: "auto" }}>
                        Correct answer
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Quiz({
  token,
  moduleId,
  questions,
  locked,
  preview,
  onCompleted,
}: {
  token: string;
  moduleId: string;
  questions: TrainingQuizQuestion[];
  locked: boolean;
  preview?: ModulePreviewConfig;
  onCompleted?: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TrainingQuizAttemptResult | null>(null);

  // Resume saved answers (CHP-365/366): seed `selected` from the trainee's
  // persisted draft. gcTime:0 is the fix — the cache entry is dropped when the
  // player unmounts, so a remount always fetches FRESH. Without it React Query
  // serves a STALE cached draft on remount and the seed-once ref locks that in,
  // so a second round of edits never appears to persist. The `!data` guard makes
  // this StrictMode-safe (data is undefined until the fresh fetch resolves, so
  // the double-invoked effect doesn't seed-and-lock prematurely). In-session
  // picks (`prev`) win; preview never reads drafts.
  const draftAnswers = useQuery<Record<string, string>>({
    queryKey: ["training", "quiz-draft", moduleId],
    queryFn: () => getTrainingQuizDraftAnswers(token, moduleId),
    enabled: !preview,
    gcTime: 0,
    staleTime: 0,
  });
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !draftAnswers.data) return;
    seededRef.current = true;
    if (Object.keys(draftAnswers.data).length > 0) {
      setSelected((prev) => ({ ...draftAnswers.data, ...prev }));
    }
  }, [draftAnswers.data]);

  // Persist picks as drafts, debounced (CHP-365/366). Coalesce rapid changes
  // into one flush after 500ms idle, keyed by question so the latest pick per
  // question wins; flush on unmount so a quick "back" doesn't drop the last pick.
  const pendingDraftsRef = useRef<Record<string, string>>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushDrafts = useCallback(() => {
    const pending = pendingDraftsRef.current;
    pendingDraftsRef.current = {};
    for (const [question_id, option_id] of Object.entries(pending)) {
      void saveTrainingQuizDraftAnswer(token, moduleId, { question_id, option_id }).catch(() => {});
    }
  }, [token, moduleId]);
  const queueDraft = useCallback(
    (questionId: string, optionId: string) => {
      pendingDraftsRef.current[questionId] = optionId;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushDrafts, 500);
    },
    [flushDrafts],
  );
  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushDrafts();
    },
    [flushDrafts],
  );

  const submit = useMutation<TrainingQuizAttemptResult>({
    mutationFn: () =>
      submitTrainingQuizAttempt(
        token,
        moduleId,
        Object.entries(selected).map(([question_id, option_id]) => ({ question_id, option_id })),
      ),
    onSuccess: async (res) => {
      setResult(res);
      if (res.completed) {
        // Await the me-status refetch so the completion screen reads the settled
        // post-completion outstanding count, not the pre-completion value (CHP-443).
        await queryClient.invalidateQueries({ queryKey: ["training", "me-status"] });
        void queryClient.invalidateQueries({ queryKey: ["training", "module-progress"] });
        onCompleted?.();
      }
    },
  });

  // Preview scores client-side against the answer key (admin-only view); no
  // attempt is recorded and the trainee status/progress queries aren't touched.
  const checkPreview = () => {
    const correct = preview?.correctByQuestion ?? {};
    const numerator = questions.filter(
      (q) => selected[q.id] && selected[q.id] === correct[q.id],
    ).length;
    setResult({
      attempt_id: "preview",
      passed: numerator === questions.length,
      score_numerator: numerator,
      score_denominator: questions.length,
      completed: false,
    });
  };

  const allAnswered = questions.every((q) => selected[q.id]);
  const passed = result?.passed ?? false;
  // The quiz is disabled until every required block is complete (RACCPE-28).
  // Preview is always interactive — never locked, and re-checkable after a pass.
  const disabled = preview ? false : locked || passed;

  return (
    <div>
      <h2 className="h2" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        Quiz
        {locked && (
          <span aria-hidden style={{ fontSize: 16 }}>
            🔒
          </span>
        )}
      </h2>
      <div className="sub" style={{ marginBottom: 14 }}>
        Answer every question correctly to pass. You may retake the quiz as many times as you need.
      </div>

      {/* CHP-953/955: until "Confirm review and unlock quiz" is pressed the quiz
          reads visually disabled (greyed + lock above) with a passive note — the
          old "Complete these first" prerequisite list is gone (CHP-955). */}
      {locked && (
        <div className="sub" style={{ marginBottom: 14 }} data-testid="quiz-locked">
          Complete the review above to unlock this quiz.
        </div>
      )}

      <div style={locked ? { opacity: 0.55, pointerEvents: "none" } : undefined}>
        {questions.map((q, qi) => (
          <div key={q.id} className="rd-card" style={{ padding: 18, marginBottom: 14 }}>
            <div id={`quiz-q-${q.id}`} style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
              {qi + 1}. {q.prompt}
            </div>
            <div
              role="radiogroup"
              aria-labelledby={`quiz-q-${q.id}`}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {q.options.map((o) => (
                <label
                  key={o.id}
                  style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}
                >
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    checked={selected[q.id] === o.id}
                    disabled={disabled}
                    onChange={() => {
                      setSelected((prev) => ({ ...prev, [q.id]: o.id }));
                      // Clear a stale fail result while the trainee re-answers.
                      setResult(null);
                      // Persist the pick so the quiz resumes here (CHP-365/366) —
                      // not in preview, which never writes drafts.
                      if (!preview) queueDraft(q.id, o.id);
                    }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
        ))}

        {result &&
          (passed ? (
            <div className="chip accent" style={{ marginBottom: 12 }} data-testid="quiz-result">
              <span className="dot ok" /> Passed {result.score_numerator}/{result.score_denominator}
              {result.completed ? " — module complete" : ""}
            </div>
          ) : (
            <div className="sub" style={{ marginBottom: 12 }} data-testid="quiz-result">
              You scored {result.score_numerator}/{result.score_denominator}. 100% is required —
              review the material and try again.
            </div>
          ))}

        {submit.isError && (
          <div className="sub" style={{ marginBottom: 12 }}>
            Couldn’t submit your answers. Please try again.
          </div>
        )}

        {preview ? (
          <button type="button" className="rd-btn" disabled={!allAnswered} onClick={checkPreview}>
            Check answers
          </button>
        ) : (
          !passed && (
            <button
              type="button"
              className="rd-btn"
              disabled={disabled || !allAnswered || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? "Submitting…" : result ? "Try again" : "Submit and finish"}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export function TraineeTrainingPlayerRoute() {
  const { moduleId } = useParams({ from: "/dashboard/partner/training/$moduleId" });
  const { accessToken, data: user } = useCurrentUser();
  // CHP-1013: wait for the role before mounting — an unresolved user would
  // mount the trainee variant for a CM and fire its progress queries before
  // viewOnly flips (react-query doesn't cancel an already-issued fetch on an
  // enabled transition).
  if (!accessToken || !user) return null;
  // A Channel Manager reads the module view-only (quiz hidden; designer ruling).
  // CHP-1049: an admin (RA/RM) reads it via the answer-key preview — quiz shown
  // with the correct answers marked, sourced from the require_admin-gated
  // preview endpoint. Trainees (channel_partner) get the interactive player.
  return (
    <ModulePlayer
      token={accessToken}
      moduleId={moduleId}
      viewOnly={user.role === "channel_manager"}
      withAnswers={user.role === "admin"}
    />
  );
}
